/**
 * Quantity Discount Offer Component
 * Shows "Buy 2 Get 10% Off" type badges next to cart items
 * Version: 1.0.0
 * 
 * Usage:
 * <quantity-discount-offer>
 *   <!-- Manages existing .qty_offer_badge elements in cart -->
 * </quantity-discount-offer>
 */

class QuantityDiscountOffer extends HTMLElement {
  constructor() {
    super();
    this.DEBUG = false;
    
    // Discount title mappings (normalized)
    this.DISCOUNT_TITLES = {
      buy2: ['buy 2 get 10% off', 'buy2dltget15%', 'buy 2 get 10% off', 'buy2dltget20%'],
      buy3or4: ['buy 3 get 15% off', 'buy 4 get 20% off']
    };
    
    // Selectors
    this.selectors = {
      badge: '.qty_offer_badge',
      cartItem: '.cart__item[data-item]',
      cartItems: 'cart-items',
      offer: '.offer',
      applyOffer: '.apply_offer',
      shippingBar: '.free-second-tier'
    };
    
    // Bind methods
    this.handleBadgeClick = this.handleBadgeClick.bind(this);
    this.initializeWithCurrentCart = this.initializeWithCurrentCart.bind(this);
    this._handleCartUpdateRaw = this._handleCartUpdateRaw.bind(this);
    
    // Create debounced version with immediate logging
    const debouncedUpdate = this.debounce(this._handleCartUpdateRaw, 150);
    this.handleCartUpdate = (event) => {
      if (this.DEBUG) console.log('[QuantityDiscountOffer] 📥 Event intercepted (before debounce):', event.type);
      debouncedUpdate.call(this, event);
    };
    
    this.lastProcessedCart = null;
    this.cartDrawerObserver = null;
    this.cartItemsObserver = null;
    this.isProcessing = false;
  }

  connectedCallback() {
    if (this.DEBUG) console.log('[QuantityDiscountOffer] 🎯 Component connected');
    
    // Read settings from data attributes
    this.isEnabled = this.getAttribute('data-enable-offer') === 'true';
    this.offer2Text = this.getAttribute('data-offer-2-text') || '';
    this.offer3Text = this.getAttribute('data-offer-3-text') || '';
    
    if (this.DEBUG) console.log('[QuantityDiscountOffer] Settings:', {
      enabled: this.isEnabled,
      hasOffer2: !!this.offer2Text,
      hasOffer3: !!this.offer3Text
    });

    if (!this.isEnabled || (!this.offer2Text && !this.offer3Text)) {
      return;
    }
    
    // MAIN STRATEGY: Watch cart-items container for DOM changes
    this.setupCartObserver();
    
    // BACKUP: Also listen to events
    document.addEventListener('cart:updated', this.handleCartUpdate);
    document.addEventListener('theme:cart:refresh', this.handleCartUpdate);
    
    // Setup click handlers
    this.setupClickHandlers();
    
    // Initialize with current cart
    this.initializeWithCurrentCart();
  }

  /**
   * Setup MutationObserver to watch cart items container
   */
  setupCartObserver() {
    // Find cart items container
    const cartItemsContainer = document.querySelector('cart-items');
    
    if (!cartItemsContainer) {
      console.warn('[QuantityDiscountOffer] ⚠️ cart-items element not found');
      return;
    }

    if (this.DEBUG) console.log('[QuantityDiscountOffer] 👁️ Setting up cart observer');

    // Throttle the observer callback to prevent excessive firing
    let observerTimeout = null;
    let lastProcessedTime = 0;
    const MIN_INTERVAL = 300; // Minimum 300ms between updates

    // Create observer to watch for changes in cart items
    this.cartItemsObserver = new MutationObserver((mutations) => {
      // Filter: Only care about changes to cart item structure, not text/attributes
      const relevantChange = mutations.some(mutation => {
        // Ignore attribute changes from loading states or other offers
        if (mutation.type === 'attributes') return false;
        
        // Ignore changes to offer message elements (from Buy X Get Y)
        if (mutation.target.classList && 
            (mutation.target.classList.contains('cart-offer-message') ||
             mutation.target.closest('.cart-offer-message'))) {
          return false;
        }
        
        // We care about structural changes (added/removed items, quantity changes)
        return mutation.type === 'childList';
      });

      if (!relevantChange) {
        return; // Skip if no relevant changes
      }

      // Throttle: Don't process more than once per MIN_INTERVAL
      const now = Date.now();
      const timeSinceLastProcess = now - lastProcessedTime;
      
      if (timeSinceLastProcess < MIN_INTERVAL) {
        // Schedule for later
        clearTimeout(observerTimeout);
        observerTimeout = setTimeout(() => {
          if (this.DEBUG) console.log('[QuantityDiscountOffer] 🔄 Cart items changed, refreshing badges');
          lastProcessedTime = Date.now();
          this.initializeWithCurrentCart();
        }, MIN_INTERVAL - timeSinceLastProcess);
      } else {
        // Process immediately
        if (this.DEBUG) console.log('[QuantityDiscountOffer] 🔄 Cart items changed, refreshing badges');
        lastProcessedTime = now;
        clearTimeout(observerTimeout);
        this.initializeWithCurrentCart();
      }
    });

    // Observe the cart items container - only childList, not subtree
    this.cartItemsObserver.observe(cartItemsContainer, {
      childList: true,  // Watch for added/removed children
      subtree: false    // Don't watch deep changes (better performance)
    });

    if (this.DEBUG) console.log('[QuantityDiscountOffer] ✓ Observer active on cart-items');
  }

  disconnectedCallback() {
    // Cleanup event listeners
    document.removeEventListener('cart:updated', this.handleCartUpdate);
    document.removeEventListener('theme:cart:refresh', this.handleCartUpdate);
    
    // Disconnect observers
    if (this.cartItemsObserver) {
      this.cartItemsObserver.disconnect();
    }
    if (this.cartDrawerObserver) {
      this.cartDrawerObserver.disconnect();
    }
    
    if (this.DEBUG) console.log('[QuantityDiscountOffer] Component disconnected');
  }

  /**
   * Initialize with current cart state
   */
  async initializeWithCurrentCart() {
    try {
      const cart = await window.CartOfferUtils.getCartData();
      if (cart) {
        await this.manageBadges(cart);
      }
    } catch (error) {
      console.error('[QuantityDiscountOffer] Error initializing:', error);
    }
  }

  /**
   * Handle cart update events (raw, will be debounced)
   */
  async _handleCartUpdateRaw(event) {
    if (this.DEBUG) console.log('[QuantityDiscountOffer] 🔔 Cart update event received:', event.type);
    
    // Wait a bit for sections to render
    await new Promise(resolve => setTimeout(resolve, 100));
    
    let cart = null;
    
    // Try to get cart from event
    if (event.detail && event.detail.cart) {
      cart = event.detail.cart;
      if (this.DEBUG) console.log('[QuantityDiscountOffer] ✓ Got cart from event detail');
    } else {
      // If event doesn't have cart data, fetch it
      if (this.DEBUG) console.log('[QuantityDiscountOffer] Fetching cart data...');
      cart = await window.CartOfferUtils.getCartData();
    }
    
    if (!cart) {
      console.warn('[QuantityDiscountOffer] ⚠️ Could not get cart data');
      return;
    }
    
    if (this.DEBUG) console.log('[QuantityDiscountOffer] Cart data:', {
      items: cart.items?.length,
      item_count: cart.item_count
    });
    
    // Prevent duplicate processing
    if (this.isSameCartState(cart)) {
      if (this.DEBUG) console.log('[QuantityDiscountOffer] ⏭️ Same cart state, skipping');
      return;
    }
    
    await this.manageBadges(cart);
  }

  /**
   * Check if cart state is same as last processed
   */
  isSameCartState(cart) {
    if (!this.lastProcessedCart) return false;
    
    return this.lastProcessedCart.item_count === cart.item_count &&
           this.lastProcessedCart.total_price === cart.total_price;
  }

  /**
   * Normalize discount title for comparison
   */
  normalizeTitle(title) {
    if (!title) return '';
    return title.toLowerCase().replace(/\s+/g, ' ').trim();
  }

  /**
   * Main badge management logic
   */
  async manageBadges(cart) {
    if (this.isProcessing) {
      if (this.DEBUG) console.log('[QuantityDiscountOffer] Already processing, skipping...');
      return;
    }

    // Check if offers are enabled
    if (!this.isEnabled) {
      return; // Silent skip if disabled
    }

    if (!this.offer2Text && !this.offer3Text) {
      return; // Silent skip if no text configured
    }

    this.isProcessing = true;

    // Wait for other offers (like Buy X Get Y) to finish their cart operations
    // This prevents conflicts when both offers are modifying the cart simultaneously
    await new Promise(resolve => setTimeout(resolve, 150));
    
    // Debug: Check if badge elements exist
    const allBadges = document.querySelectorAll('.qty_offer_badge');
    if (this.DEBUG) console.log('[QuantityDiscountOffer] Found badge elements in DOM:', allBadges.length);
    if (allBadges.length === 0) {
      this.isProcessing = false;
      return;
    }
    
    if (this.DEBUG) console.log('[QuantityDiscountOffer] Managing badges for cart:', {
      itemCount: cart.item_count,
      items: cart.items?.length
    });

    // 1) Check if any cart item has _Offer property (from Buy X Get Y)
    const hasOfferProperty = this.checkForOfferProperties(cart);
    
    // 2) Build per-variant discount state
    const variantState = this.buildVariantState(cart);
    
    // 3) Check if special discount is active (hide all badges)
    const hideAllBadges = this.shouldHideAllBadges(variantState);
    
    if (hideAllBadges) {
      if (this.DEBUG) console.log('[QuantityDiscountOffer] Special discount active, hiding all badges');
      this.hideAllBadges();
      this.hideShippingBar(cart);
      this.lastProcessedCart = {
        item_count: cart.item_count,
        total_price: cart.total_price
      };
      this.isProcessing = false;
      return;
    }
    
    // 4) Show/hide badges per variant
    this.updateBadgeVisibility(variantState);
    
    // 5) Re-attach click handlers (DOM might have been updated)
    this.setupClickHandlers();
    
    // 6) Manage shipping bar visibility
    this.hideShippingBar(cart);
    
    // Store cart state
    this.lastProcessedCart = {
      item_count: cart.item_count,
      total_price: cart.total_price
    };

    this.isProcessing = false;
  }

  /**
   * Check if cart has any _Offer properties
   */
  checkForOfferProperties(cart) {
    if (!cart || !Array.isArray(cart.items)) return false;
    
    for (const item of cart.items) {
      if (item.properties && typeof item.properties === 'object') {
        for (const key in item.properties) {
          if (key && key.startsWith('_Offer')) {
            return true;
          }
        }
      }
    }
    
    return false;
  }

  /**
   * Build discount state for each variant
   */
  buildVariantState(cart) {
    const variantState = {};
    
    if (!cart || !Array.isArray(cart.items)) return variantState;
    
    for (const item of cart.items) {
      const variantId = item.variant_id;
      if (!variantId) continue;
      
      const state = variantState[variantId] || (variantState[variantId] = {
        hasBuy2: false,
        hasBuy3or4: false
      });
      
      // Check item discounts
      if (Array.isArray(item.discounts)) {
        for (const discount of item.discounts) {
          const title = this.normalizeTitle(discount.title);
          
          // Check if it's a Buy 2 discount
          if (this.DISCOUNT_TITLES.buy2.includes(title)) {
            state.hasBuy2 = true;
          }
          
          // Check if it's a Buy 3 or 4 discount
          if (this.DISCOUNT_TITLES.buy3or4.includes(title)) {
            state.hasBuy3or4 = true;
          }
        }
      }
      
      variantState[variantId] = state;
    }
    
    if (this.DEBUG) console.log('[QuantityDiscountOffer] Variant state:', variantState);
    
    return variantState;
  }

  /**
   * Check if all badges should be hidden (special discount active)
   */
  shouldHideAllBadges(variantState) {
    for (const variantId in variantState) {
      const state = variantState[variantId];
      if (state.hasBuy2) {
        return true;
      }
    }
    return false;
  }

  /**
   * Hide all badge containers
   */
  hideAllBadges() {
    const badges = document.querySelectorAll(this.selectors.badge);
    badges.forEach(badge => {
      badge.style.display = 'none';
    });
  }

  /**
   * Update badge visibility based on variant state
   */
  updateBadgeVisibility(variantState) {
    // Reset all badges first
    const badges = document.querySelectorAll(this.selectors.badge);
    badges.forEach(container => {
      container.style.display = 'none';
      container.querySelectorAll(this.selectors.offer).forEach(offer => {
        offer.style.display = 'none';
      });
      container.querySelectorAll(this.selectors.applyOffer).forEach(link => {
        link.style.display = '';
      });
    });
    
    // Show badges per variant (only first occurrence)
    const shownVariants = new Set();
    const cartItems = document.querySelectorAll(this.selectors.cartItem);
    
    cartItems.forEach(itemEl => {
      // Skip hidden items
      if (itemEl.offsetParent === null) return;
      
      // Get variant ID from data-item attribute
      const dataItem = itemEl.getAttribute('data-item') || '';
      const variantId = parseInt(dataItem.split(':')[0], 10);
      
      if (!variantId || shownVariants.has(variantId)) return;
      
      // Find badge container
      const container = itemEl.querySelector(this.selectors.badge);
      if (!container) return;
      
      // Get state for this variant
      const state = variantState[variantId] || { hasBuy2: false, hasBuy3or4: false };
      
      // If has Buy 3/4 discount, don't show any badges
      if (state.hasBuy3or4) {
        return;
      }
      
      // Find badge wrappers
      const buy2Wrap = this.getBadgeWrapper(container, 'buy2');
      const buy3Wrap = this.getBadgeWrapper(container, 'buy3');
      
      // Show container
      container.style.display = '';
      
      // Show/hide specific badges based on state
      if (state.hasBuy2) {
        // Has Buy 2 discount: Hide buy2 badge, show buy3 badge
        if (buy2Wrap) buy2Wrap.style.display = 'none';
        if (buy3Wrap) buy3Wrap.style.display = '';
      } else {
        // No discount: Show both badges
        if (buy2Wrap) buy2Wrap.style.display = '';
        if (buy3Wrap) buy3Wrap.style.display = '';
      }
      
      shownVariants.add(variantId);
      
      if (this.DEBUG) console.log('[QuantityDiscountOffer] Showing badges for variant:', {
        variantId,
        state,
        buy2Visible: !state.hasBuy2,
        buy3Visible: true
      });
    });
  }

  /**
   * Get badge wrapper element
   */
  getBadgeWrapper(container, badgeType) {
    const link = container.querySelector(`${this.selectors.applyOffer}[data-badge="${badgeType}"]`);
    return link ? link.closest(this.selectors.offer) : null;
  }

  /**
   * Hide/show shipping bar based on discounts
   */
  hideShippingBar(cart) {
    const targetDiscounts = ['buy 3 get 15% off', 'buy 2 get 10% off', 'buy 4 get 20% off'];
    let discountApplied = false;
    
    if (cart.items && cart.items.length > 0) {
      for (const item of cart.items) {
        if (item.discounts && item.discounts.length > 0) {
          for (const discount of item.discounts) {
            if (targetDiscounts.includes(discount.title)) {
              discountApplied = true;
              break;
            }
          }
        }
        if (discountApplied) break;
      }
    }
    
    const shippingBar = document.querySelector(this.selectors.shippingBar);
    if (shippingBar) {
      if (discountApplied) {
        shippingBar.classList.add('hide-shipping-bar');
      } else {
        shippingBar.classList.remove('hide-shipping-bar');
      }
    }
  }

  /**
   * Setup click handlers for badge buttons
   */
  setupClickHandlers() {
    const cartContainer = document.querySelector(this.selectors.cartItems);
    if (!cartContainer) return;
    
    // Remove old listener first to avoid duplicates
    cartContainer.removeEventListener('click', this.handleBadgeClick);
    
    // Use event delegation
    cartContainer.addEventListener('click', this.handleBadgeClick);
  }

  /**
   * Handle badge button click
   */
  handleBadgeClick(event) {
    const offerBtn = event.target.closest(this.selectors.applyOffer);
    if (!offerBtn) return;
    
    event.preventDefault();
    
    const targetQty = parseInt(offerBtn.getAttribute('data-qty'), 10);
    const cartItem = offerBtn.closest(this.selectors.cartItem);
    
    if (!cartItem) return;
    
    const qtyInput = cartItem.querySelector('.cart__quantity-field');
    if (!qtyInput) return;
    
    const currentQty = parseInt(qtyInput.value, 10);
    if (currentQty === targetQty) return;
    
    if (this.DEBUG) console.log('[QuantityDiscountOffer] Updating quantity:', {
      currentQty,
      targetQty
    });
    
    // Update quantity
    qtyInput.value = targetQty;
    
    // Trigger change event
    const changeEvent = new Event('change', { bubbles: true });
    qtyInput.dispatchEvent(changeEvent);
  }

  /**
   * Debounce utility function
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const context = this;
      const later = () => {
        clearTimeout(timeout);
        func.apply(context, args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Register custom element
if (!customElements.get('quantity-discount-offer')) {
  customElements.define('quantity-discount-offer', QuantityDiscountOffer);
}
